/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { AppointmentInterface, ProgressNoteInterface } from "@malmur-technologies/medicalsystem-shared";

/**
 * Properties for the PrintPrescriptions template component.
 * 
 * @interface PrintTemplateProps
 * @property {Partial<AppointmentInterface>} appointmentModel - Appointment data (patient, provider, vitals, diagnosis)
 * @property {Partial<ProgressNoteInterface> | null} progressNoteModel - Progress note data
 * @property {number} age - Patient age in years
 * @property {string} medicationTest - Medication prescription HTML content
 * @property {Function} t - Translation function for i18n
 * @property {string} [labTest] - Lab test orders HTML content
 * @property {string} [imagingTest] - Imaging test orders HTML content
 * @property {boolean} printLabTest - Whether to include lab test section
 * @property {boolean} printMedication - Whether to include medication section
 * @property {boolean} printImagingTest - Whether to include imaging test section
 * @property {string} [notesMedicine] - Additional notes for medication section
 * @property {string} [notesLab] - Additional notes for lab test section
 * @property {string} [notesImaging] - Additional notes for imaging test section
 */
interface PrintTemplateProps {
  appointmentModel: Partial<AppointmentInterface>;
  progressNoteModel: Partial<ProgressNoteInterface> | null;
  age: number;
  medicationTest: string;
  t: (key: string) => string;
  labTest?: string;
  imagingTest?: string;
  printLabTest: boolean;
  printMedication: boolean;
  printImagingTest: boolean;
  notesMedicine?: string;
  notesLab?: string;
  notesImaging?: string;
  previewImageUrl?: string;
}

/**
 * Generates printable prescription template with duplicate copies (original + copy).
 * Creates formatted HTML document for printing prescriptions, lab orders, and imaging orders.
 * 
 * Features:
 * - Professional medical prescription format
 * - Original and copy versions for patient/office records
 * - Three prescription types: medication, lab tests, imaging tests
 * - Doctor header with credentials and facility info
 * - Patient demographics and vital signs
 * - Diagnosis section
 * - Treatment/prescription details
 * - Optional comments/notes per section
 * - Signature area for doctor
 * - Print-friendly styling with page breaks
 * - Responsive layout for letter-sized paper
 * 
 * Layout Structure:
 * - Doctor information (name, specialty, license)
 * - Facility address and date
 * - Patient name and age
 * - Vital signs table (BP, HR, Temp, Oxy, Wt, Ht, BMI)
 * - Diagnosis
 * - Treatment/prescription content
 * - Comments section (if provided)
 * - Doctor signature area
 * 
 * Duplicate Logic:
 * - Original: Labeled "ORIGINAL" for office records
 * - Copy: Labeled "COPY" for patient
 * - Each section generates both versions
 * 
 * Print Sections (conditionally included):
 * - Medication prescription (if printMedication=true)
 * - Lab test orders (if printLabTest=true)
 * - Imaging test orders (if printImagingTest=true)
 * - Page breaks between sections for clean printing
 * 
 * @param {PrintTemplateProps} props - Template properties
 * @returns {string} Complete HTML document ready for printing
 */
export const PrintPrescriptions = (props: PrintTemplateProps) => {
  const {
    appointmentModel,
    progressNoteModel,
    age,
    medicationTest,
    t,
    labTest,
    imagingTest,
    printLabTest,
    printMedication,
    printImagingTest,
    notesMedicine,
    notesLab,
    notesImaging,
    previewImageUrl
  } = props;
  const style = ` <style>
    body {
      font-family: Arial, sans-serif;
      margin-top: 0px;
      padding: 0;
    }
.print-container {
  margin: 0;
  max-width: 700px;
  background: #fff;
  padding: 8px;
}
.doctor-header {
  font-size: 1.1em;
  font-weight: bold;
  text-align: center;
  margin-bottom: 2px;
  margin-top: 2px;
}
.doctor-details-row {
  font-size: 0.98em;
  margin-bottom: 2px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.divider {
  border: none;
  border-top: 2px solid #7b809a;
  margin: 4px 0 8px 0;
}
.patient-row {
  font-size: 1em;
  margin-bottom: 6px;
  text-align: center;
}
.vitals-section {
  margin-bottom: 8px;
}
.diagnosis-section,
.treatment-section,
.comments-section {
  line-height: 1.3;
}

.diagnosis-section strong,
.treatment-section strong,
.comments-section strong {
  margin: 0;
  padding: 0;
  display: block;
}

.diagnosis-section p,
.treatment-section p,
.comments-section p {
  margin: 0;
  padding: 0;
}
.vitals-table {
  display: flex;
  flex-wrap: wrap;
  margin-top: 2px;
}
.vitals-table > div {
  width: 14%;
  box-sizing: border-box;
  margin-bottom: 4px;
  font-size: 0.96em;
}
.dotted-divider {
  border: none;
  border-top: 2px dotted #7b809a;
  margin: 12px 0 0 0;
}
.signature-rect {
  width: 320px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  margin-top: 32px;
  margin-left: auto;
}
.copy-label {
  width: 60px;
  font-size: 1em;
  color: #7b809a;
  font-weight: bold;
  letter-spacing: 1px;
  text-align: left;
  margin-bottom: 4px;
  white-space: nowrap;
}
.original-label {
  width: 80px;
  font-size: 1em;
  color: #7b809a;
  font-weight: bold;
  letter-spacing: 1px;
  text-align: left;
  margin-bottom: 4px;
  white-space: nowrap;
}
.signature-area {
  width: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.signature-line {
  width: 220px;
  border-bottom: 1.5px solid #7b809a;
  margin-bottom: 2px;
}

.signature-label {
  font-size: 0.95em;
  color: #555;
  margin-top: 2px;
  text-align: center;
  width: 100%;
}
 .treatment-hpi {
  margin: 0;
  padding: -100px;
  font-size: 1em;
  line-height: 1.3;
}
.treatment-hpi br {
  line-height: 1.1;
  margin: 0;
  padding: 0;
}
.treatment-hpi p {
  margin: 0 !important;
  padding: 0 !important;
}
    </style>`;
  const prescriptionsTemplateHTML = (prescription: string, title: string, notes: string) => {
    return `<div class="print-container">
      <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 10px;">
        ${previewImageUrl ? `<div style="flex-shrink: 0; margin-right: 15px;">
          <img src="${previewImageUrl}" alt="Doctor's Image" style="max-width: 100px; max-height: 100px; object-fit: contain;" />
        </div>` : ""}
        <div style="display: flex; flex-direction: column; justify-content: center; text-align: center;">
          <div style="font-size: 1.1em; font-weight: bold;">${t("progressNoteForm.dr")} ${appointmentModel?.provider?.person?.name || "Nombre"} ${appointmentModel?.provider?.person?.lastname || " "}</div>
          <div style="font-size: 0.98em;">${appointmentModel?.provider?.specialty || t("progressNoteForm.specialty")}</div>
          <div style="font-size: 0.98em;">${t("progressNoteForm.medicalLicense")}: ${appointmentModel?.provider?.medicalLicense || "0000000"}</div>
        </div>
      </div>
      
      <div class="doctor-details-row">
        <span>
            ${t("progressNoteForm.address")}: ${appointmentModel?.facility?.address || t("progressNoteForm.defaultAddress")}
        </span>
        <span>
          ${t("progressNoteForm.date")}:  ${new Date().toLocaleDateString()}
        </span>
      </div>
      <hr class="divider" />

      <div class="patient-row">
        ${t("progressNoteForm.patient")}: ${appointmentModel?.patient?.person?.name || "-"}  ${appointmentModel?.patient?.person?.lastname || "-"} 
        ,
        ${t("progressNoteForm.age")}: ${age} ${t("progressNoteForm.years")}
      </div>

      <div class="vitals-section">
        <strong>${t("progressNoteForm.vitals")}</strong>
        <div class="vitals-table">
          <div>${t("progressNoteForm.bp")}: ${appointmentModel?.vitals?.bp || "-"}</div>
          <div>${t("progressNoteForm.hr")}: ${appointmentModel?.vitals?.hr || "-"}</div>
          <div>${t("progressNoteForm.temp")}: ${appointmentModel?.vitals?.temp || "-"}</div>
          <div>${t("progressNoteForm.oxy")}: ${appointmentModel?.vitals?.oxy || "-"}</div>
          <div>${t("progressNoteForm.wt")}: ${appointmentModel?.vitals?.wt || "-"}</div>
          <div>${t("progressNoteForm.ht")}: ${appointmentModel?.vitals?.ht || "-"}</div>
          <div>${t("progressNoteForm.bmi")}: ${appointmentModel?.vitals?.bmi || "-"}</div>
        </div>
      </div>

      <div class="diagnosis-section">
        <strong>${t("progressNoteForm.diagnosis")}</strong>
        <p>${appointmentModel?.diagnosis || "-"}</p>
      </div>
   
      <div class="treatment-section">
        <strong>${title}</strong>
        <div class="treatment-hpi">${prescription || "-"}</div>
      </div>
      ${
        notes
          ? `<div class="comments-section">
            <strong>${t("progressNoteForm.comments")}</strong>
            <p>${notes}</p>
          </div>`
          : ""
      }
<div class="signature-rect">
  <span class="original-label">${t("progressNoteForm.original")}</span>
  <div class="signature-area">
    <div class="signature-line"></div>
    <div class="signature-label">${t("progressNoteForm.doctorSignature")}</div>
  </div>
</div>
      <hr class="dotted-divider" />
    </div>

         <div class="print-container">
 <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 10px;">
        ${previewImageUrl ? `<div style="flex-shrink: 0; margin-right: 15px;">
          <img src="${previewImageUrl}" alt="Doctor's Image" style="max-width: 100px; max-height: 100px; object-fit: contain;" />
        </div>` : ""}
        <div style="display: flex; flex-direction: column; justify-content: center; text-align: center;">
          <div style="font-size: 1.1em; font-weight: bold;">${t("progressNoteForm.dr")} ${appointmentModel?.provider?.person?.name || "Nombre"} ${appointmentModel?.provider?.person?.lastname || " "}</div>
          <div style="font-size: 0.98em;">${appointmentModel?.provider?.specialty || t("progressNoteForm.specialty")}</div>
          <div style="font-size: 0.98em;">${t("progressNoteForm.medicalLicense")}: ${appointmentModel?.provider?.medicalLicense || "0000000"}</div>
        </div>
      </div>
      
      <div class="doctor-details-row">
        <span>
            ${t("progressNoteForm.address")}: ${appointmentModel?.facility?.address || t("progressNoteForm.defaultAddress")}
        </span>
        <span>
          ${t("progressNoteForm.date")}:  ${new Date().toLocaleDateString()}
        </span>
      </div>
      <hr class="divider" />

      <div class="patient-row">
        ${t("progressNoteForm.patient")}: ${appointmentModel?.patient?.person?.name || "-"}  ${appointmentModel?.patient?.person?.lastname || "-"} 
        ,
        ${t("progressNoteForm.age")}: ${age}  ${t("progressNoteForm.years")}
      </div>

      <div class="vitals-section">
        <strong>${t("progressNoteForm.vitals")}</strong>
        <div class="vitals-table">
          <div>${t("progressNoteForm.bp")}: ${appointmentModel?.vitals?.bp || "-"}</div>
          <div>${t("progressNoteForm.hr")}: ${appointmentModel?.vitals?.hr || "-"}</div>
          <div>${t("progressNoteForm.temp")}: ${appointmentModel?.vitals?.temp || "-"}</div>
          <div>${t("progressNoteForm.oxy")}: ${appointmentModel?.vitals?.oxy || "-"}</div>
          <div>${t("progressNoteForm.wt")}: ${appointmentModel?.vitals?.wt || "-"}</div>
          <div>${t("progressNoteForm.ht")}: ${appointmentModel?.vitals?.ht || "-"}</div>
          <div>${t("progressNoteForm.bmi")}: ${appointmentModel?.vitals?.bmi || "-"}</div>
        </div>
      </div>

      <div class="diagnosis-section">
        <strong>${t("progressNoteForm.diagnosis")}</strong>
        <p>${appointmentModel?.diagnosis || "-"}</p>
      </div>
   
      <div class="treatment-section">
        <strong>${title}</strong>
        <div class="treatment-hpi">${prescription || "-"}</div>
      </div>
      ${
        notes
          ? `<div class="comments-section">
            <strong>${t("progressNoteForm.comments")}</strong>
            <p>${notes}</p>
          </div>`
          : ""
      }
<div class="signature-rect">
  <span class="copy-label">${t("progressNoteForm.copy")}</span>
  <div class="signature-area">
    <div class="signature-line"></div>
    <div class="signature-label">${t("progressNoteForm.doctorSignature")}</div>
  </div>
</div>`;
  };

  return `
<html>
  <head>
    <meta charset="UTF-8">
    <title>.</title>
    ${style}
  </head>
  <body>
      ${
        printMedication
          ? ` ${prescriptionsTemplateHTML(medicationTest, t("progressNoteForm.treatment"), notesMedicine)} 
          ${printLabTest || printImagingTest ? '<div style="page-break-before: always; break-before: page;"></div>' : ""}`
          : ""
      }
      ${
        printLabTest
          ? ` ${prescriptionsTemplateHTML(labTest, t("progressNoteForm.labTest"), notesLab)}
          ${printImagingTest ? '<div style="page-break-before: always; break-before: page;"></div>' : ""}`
          : ""
      }
      ${
        printImagingTest
          ? ` ${prescriptionsTemplateHTML(imagingTest, t("progressNoteForm.imagingTest"), notesImaging)}`
          : ""
      }
        `;
};
export default PrintPrescriptions;
